import { BUILDINGS, UNITS } from "./content/buildings.js";
import { effectsForResearch } from "./research-effects.js";
import type { SeatObservation } from "./types.js";

export interface QueueEtaProjection {
  readonly ratePerSecond: string;
  readonly etaSeconds: number | null;
  readonly explanation: string;
}

export function queueEtaProjection(
  observation: SeatObservation,
  queue: "construction" | "restoration" | "assembly",
): QueueEtaProjection | null {
  const order = observation.self.manufacturing[queue][0];
  if (!order) return null;
  const rule =
    queue === "assembly"
      ? UNITS.find((entry) => entry.id === order.target)
      : BUILDINGS.find((entry) => entry.id === order.target);
  if (!rule) return null;
  const economy = observation.self.economy;
  let workRate = numeric(economy.constructionWork);
  let metalRate = numeric(economy.metalLanes.construction.received);
  let workCost = rule.workCost;
  let metalCost = rule.metalCost;
  if (queue === "assembly") {
    workRate = numeric(economy.assemblyWork);
    metalRate = numeric(economy.metalLanes["troop-assembly"].received);
  } else {
    const split = observation.self.constructionSplit;
    workRate *= (queue === "construction" ? split.newConstruction : split.restoration) / 100;
    if (queue === "restoration") {
      workCost *= 0.5;
      metalCost *= Number(
        effectsForResearch(observation.self.research).ratios["restoration-metal"] ?? "0.5",
      );
    }
    metalRate *= constructionDemandShare(observation, queue);
  }
  const byWork = workCost === 0 ? Number.POSITIVE_INFINITY : workRate / workCost;
  const byMetal = metalCost === 0 ? Number.POSITIVE_INFINITY : metalRate / metalCost;
  const rate = Math.max(0, Math.min(byWork, byMetal));
  const etaSeconds = rate > 0 ? Math.ceil(numeric(order.remaining) / rate) : null;
  return {
    ratePerSecond: format(rate),
    etaSeconds,
    explanation: `Current conditional rate includes both ${format(workRate)} Work/s and ${format(metalRate)} Metal/s limits.`,
  };
}

function constructionDemandShare(
  observation: SeatObservation,
  selected: "construction" | "restoration",
): number {
  const demands = (["construction", "restoration"] as const).map((queue) => {
    const order = observation.self.manufacturing[queue][0];
    const rule = order && BUILDINGS.find((entry) => entry.id === order.target);
    if (!order || !rule) return 0;
    const split = observation.self.constructionSplit;
    const allocated = queue === "construction" ? split.newConstruction : split.restoration;
    const workRatio = queue === "restoration" ? 0.5 : 1;
    const metalRatio =
      queue === "restoration"
        ? Number(effectsForResearch(observation.self.research).ratios["restoration-metal"] ?? "0.5")
        : 1;
    const output =
      (numeric(observation.self.economy.constructionWork) * allocated) /
      100 /
      (rule.workCost * workRatio);
    return output * rule.metalCost * metalRatio;
  });
  const total = (demands[0] ?? 0) + (demands[1] ?? 0);
  return total > 0 ? (demands[selected === "construction" ? 0 : 1] ?? 0) / total : 0;
}

function numeric(value: string): number {
  const result = Number(value);
  return Number.isFinite(result) ? result : 0;
}

function format(value: number): string {
  return value.toFixed(6).replace(/\.?0+$/, "");
}
